import { z } from 'zod'
import { token2022ProgramId, tokenProgramId } from '../constants'
import type { Instruction } from '../schemas/instruction'
import type { TransactionDetail } from '../schemas/transactionDetail'

/** SPL Token数量の10進文字列表現。 */
const amountSchema = z.string().regex(/^\d+$/)
/** 計数対象6 instructionのinfo schema。 */
const instructionInfoSchemas = {
  transfer: z.object({
    source: z.string().min(1),
    destination: z.string().min(1),
    amount: amountSchema,
  }),
  transferChecked: z.object({
    source: z.string().min(1),
    destination: z.string().min(1),
    mint: z.string().min(1),
    tokenAmount: z.object({ amount: amountSchema }),
  }),
  mintTo: z.object({
    account: z.string().min(1),
    mint: z.string().min(1),
    amount: amountSchema,
  }),
  mintToChecked: z.object({
    account: z.string().min(1),
    mint: z.string().min(1),
    tokenAmount: z.object({ amount: amountSchema }),
  }),
  burn: z.object({
    account: z.string().min(1),
    mint: z.string().min(1),
    amount: amountSchema,
  }),
  burnChecked: z.object({
    account: z.string().min(1),
    mint: z.string().min(1),
    tokenAmount: z.object({ amount: amountSchema }),
  }),
} as const

/** 対象Token instructionが正常解析できる場合にtrueを返す。 */
const isCountableInstruction = ({
  instruction,
}: {
  readonly instruction: Instruction
}): boolean => {
  if (
    !('parsed' in instruction) ||
    (instruction.programId !== tokenProgramId &&
      instruction.programId !== token2022ProgramId)
  ) {
    return false
  }
  switch (instruction.parsed.type) {
    case 'transfer':
      return instructionInfoSchemas.transfer.safeParse(instruction.parsed.info)
        .success
    case 'transferChecked':
      return instructionInfoSchemas.transferChecked.safeParse(
        instruction.parsed.info,
      ).success
    case 'mintTo':
      return instructionInfoSchemas.mintTo.safeParse(instruction.parsed.info)
        .success
    case 'mintToChecked':
      return instructionInfoSchemas.mintToChecked.safeParse(
        instruction.parsed.info,
      ).success
    case 'burn':
      return instructionInfoSchemas.burn.safeParse(instruction.parsed.info)
        .success
    case 'burnChecked':
      return instructionInfoSchemas.burnChecked.safeParse(
        instruction.parsed.info,
      ).success
    default:
      return false
  }
}

/**
 * 取得済みSolana transactionの元instructionからToken Transfer件数を数える。
 *
 * @param transaction jsonParsed形式のtransaction全体。
 * @returns top-levelとinnerにある対象6種の正常なinstruction件数。
 */
export const countTokenTransferInstructions = ({
  transaction,
}: {
  readonly transaction: TransactionDetail
}): number => {
  if (transaction.meta?.err !== null) {
    return 0
  }

  let count = 0
  for (const instruction of transaction.transaction.message.instructions) {
    if (isCountableInstruction({ instruction })) {
      count += 1
    }
  }
  for (const inner of transaction.meta.innerInstructions ?? []) {
    for (const instruction of inner.instructions) {
      if (isCountableInstruction({ instruction })) {
        count += 1
      }
    }
  }
  return count
}
